/**
 * Padding applied via temporary scroll-margin so nearest scroll leaves room for
 * the guide tooltip instead of gluing the target to the viewport edge.
 * Kept smaller than a full `block: 'center'` jump to avoid displacing nested
 * scroll containers / fixed chrome on customer sites (e.g. Kigi).
 */
export const GUIDE_SCROLL_MARGIN_PX = 200;

/**
 * Sub-pixel slack for the comfort check. Browsers land a `nearest` scroll with the
 * scroll-margin box flush against the scrollport edge, so the target sits at exactly
 * `margin` px from it -- give or take a device pixel (fractional DPR, fractional
 * layout offsets). Without slack that rounding reads as "still uncomfortable" and
 * costs an extra scroll + settle round on every step.
 */
export const GUIDE_SCROLL_COMFORT_TOLERANCE_PX = 2;

export const GUIDE_SCROLL_BLOCK: ScrollLogicalPosition = 'nearest';
export const GUIDE_SCROLL_INLINE: ScrollLogicalPosition = 'nearest';

const SCROLL_MARGIN_LONGHANDS = [
  'scroll-margin-top',
  'scroll-margin-right',
  'scroll-margin-bottom',
  'scroll-margin-left'
] as const;

/** overflow values that make an ancestor a scroll container (`clip` does not). */
const SCROLL_CONTAINER_OVERFLOWS = ['auto', 'scroll', 'hidden', 'overlay'];

export type GuideScrollRect = {
  x: number;
  y: number;
  width: number;
  height: number;
};

export type GuideScrollViewport = {
  width: number;
  height: number;
};

/** Effective scroll-margin per axis, already clamped to what the scrollport can honour. */
export type GuideScrollMargin = {
  block: number;
  inline: number;
};

const getDefaultViewport = (): GuideScrollViewport => ({
  width: typeof window !== 'undefined' ? window.innerWidth : 0,
  height: typeof window !== 'undefined' ? window.innerHeight : 0
});

const normalizeMargin = (margin: number | GuideScrollMargin): GuideScrollMargin =>
  typeof margin === 'number' ? { block: margin, inline: margin } : margin;

/**
 * True when the target is outside the viewport, or (when it fits) closer to an
 * edge than the comfort margin — not enough room for a typical tooltip.
 * Oversized targets that cannot satisfy the margin on both sides are not
 * treated as uncomfortable, to avoid scroll loops.
 */
export function isGuideTargetUncomfortable(
  dimensions: GuideScrollRect,
  viewport: GuideScrollViewport = getDefaultViewport(),
  margin: number | GuideScrollMargin = GUIDE_SCROLL_MARGIN_PX
): boolean {
  const { block: blockMargin, inline: inlineMargin } = normalizeMargin(margin);
  const tolerance = GUIDE_SCROLL_COMFORT_TOLERANCE_PX;
  const top = dimensions.y;
  const left = dimensions.x;
  const bottom = dimensions.y + dimensions.height;
  const right = dimensions.x + dimensions.width;

  if (bottom < 0 || top > viewport.height || right < 0 || left > viewport.width) {
    return true;
  }

  const fitsVertically = dimensions.height <= viewport.height - 2 * blockMargin;
  const fitsHorizontally = dimensions.width <= viewport.width - 2 * inlineMargin;

  if (
    fitsVertically &&
    (top < blockMargin - tolerance || bottom > viewport.height - blockMargin + tolerance)
  ) {
    return true;
  }

  if (
    fitsHorizontally &&
    (left < inlineMargin - tolerance || right > viewport.width - inlineMargin + tolerance)
  ) {
    return true;
  }

  return false;
}

/**
 * Smallest scrollport the target has to fit in: every scrolling ancestor that
 * scrollIntoView will actually align inside (a side panel, a modal body, a table with
 * max-height) plus the viewport itself, read from the element's own document so it also
 * works inside an iframe. An ancestor that merely clips (overflow hidden with nothing to
 * scroll, e.g. a card with rounded corners) is skipped: aligning inside it is a no-op.
 */
const resolveScrollportSize = (element: HTMLElement): GuideScrollViewport => {
  const view = element.ownerDocument?.defaultView;
  if (!view) return { width: 0, height: 0 };

  let width = view.innerWidth;
  let height = view.innerHeight;
  let ancestor = element.parentElement;

  while (ancestor) {
    const style = view.getComputedStyle(ancestor);
    if (
      SCROLL_CONTAINER_OVERFLOWS.includes(style.overflowY) &&
      ancestor.clientHeight > 0 &&
      ancestor.scrollHeight > ancestor.clientHeight
    ) {
      height = height > 0 ? Math.min(height, ancestor.clientHeight) : ancestor.clientHeight;
    }
    if (
      SCROLL_CONTAINER_OVERFLOWS.includes(style.overflowX) &&
      ancestor.clientWidth > 0 &&
      ancestor.scrollWidth > ancestor.clientWidth
    ) {
      width = width > 0 ? Math.min(width, ancestor.clientWidth) : ancestor.clientWidth;
    }
    ancestor = ancestor.parentElement;
  }

  return { width, height };
};

/**
 * `block/inline: 'nearest'` aligns the scroll-MARGIN box, not the element. A margin
 * box taller than the scrollport therefore parks the element itself outside the
 * visible area (short viewport, dropdown listbox, panel with max-height) — the exact
 * opposite of what the comfort margin is for. So the margin is clamped per axis to
 * what still fits on both sides of the target.
 */
export function resolveGuideScrollMargin(element: HTMLElement): GuideScrollMargin {
  const scrollport = resolveScrollportSize(element);
  const rect = element.getBoundingClientRect();
  const clamp = (available: number) =>
    Math.max(0, Math.min(GUIDE_SCROLL_MARGIN_PX, Math.floor(available / 2)));

  return {
    block: clamp(scrollport.height - rect.height),
    inline: clamp(scrollport.width - rect.width)
  };
}

type ClippedScrollPosition = {
  element: Element;
  top: number;
  left: number;
};

/** Parent element, stepping out of a shadow root to its host like scrollIntoView does. */
const getParentAcrossShadow = (node: Element): Element | null => {
  if (node.parentElement) return node.parentElement;
  const root = node.getRootNode();
  return root instanceof ShadowRoot ? root.host : null;
};

/**
 * `scrollIntoView` also scrolls ancestors with `overflow: hidden`, and the visitor has
 * no scrollbar to undo it. An Angular Material expansion panel (48px tall, overflow
 * hidden) whose target sits in its collapsed body ends up with the header scrolled out
 * of its own box -- the menu item "disappears" and stops being clickable. Snapshot those
 * clipping ancestors so the scroll can be taken back right after. The root elements are
 * left alone: a page that locks body scroll still needs the viewport to follow the step.
 */
const collectClippedScrollPositions = (element: HTMLElement): ClippedScrollPosition[] => {
  const doc = element.ownerDocument;
  const view = doc?.defaultView;
  if (!doc || !view) return [];

  const positions: ClippedScrollPosition[] = [];
  let ancestor = getParentAcrossShadow(element);

  while (ancestor) {
    if (ancestor !== doc.body && ancestor !== doc.documentElement) {
      const style = view.getComputedStyle(ancestor);
      if (style.overflowY === 'hidden' || style.overflowX === 'hidden') {
        positions.push({ element: ancestor, top: ancestor.scrollTop, left: ancestor.scrollLeft });
      }
    }
    ancestor = getParentAcrossShadow(ancestor);
  }

  return positions;
};

/**
 * Put clipping ancestors back where the page left them. Assign unconditionally: a smooth
 * scroll has not moved yet when the call returns, and the assignment is what cancels it
 * on that ancestor -- without stopping the one on the real scroll container (verified
 * in Chrome).
 */
const restoreClippedScrollPositions = (positions: ClippedScrollPosition[]) => {
  positions.forEach(({ element, top, left }) => {
    element.scrollTop = top;
    element.scrollLeft = left;
  });
};

export function scrollElementIntoViewForGuide(
  element: HTMLElement,
  behavior: ScrollBehavior = 'smooth'
): void {
  const margin = resolveGuideScrollMargin(element);
  const clippedPositions = collectClippedScrollPositions(element);
  // Longhands, not the `scroll-margin` shorthand: the shorthand getter serializes to ''
  // when the host page set only some sides inline, and restoring '' would then delete
  // the customer's own scroll-margin-top instead of putting it back.
  const previous = SCROLL_MARGIN_LONGHANDS.map(property => ({
    property,
    value: element.style.getPropertyValue(property),
    priority: element.style.getPropertyPriority(property)
  }));

  element.style.setProperty('scroll-margin-top', `${margin.block}px`);
  element.style.setProperty('scroll-margin-bottom', `${margin.block}px`);
  element.style.setProperty('scroll-margin-left', `${margin.inline}px`);
  element.style.setProperty('scroll-margin-right', `${margin.inline}px`);

  try {
    element.scrollIntoView({
      behavior,
      block: GUIDE_SCROLL_BLOCK,
      inline: GUIDE_SCROLL_INLINE
    });
  } finally {
    restoreClippedScrollPositions(clippedPositions);
    // Target is resolved synchronously from current scroll-margin; restore so we
    // do not leave host-page styles behind after the guide step.
    previous.forEach(({ property, value, priority }) => {
      if (value) {
        element.style.setProperty(property, value, priority);
      } else {
        element.style.removeProperty(property);
      }
    });
  }
}
